import type { CaptureSequence, CustomLandmarks, Landmark, PelvisPinKey, PelvisPinsMeta } from '../types';
import { IDX } from '../constants';

const ALL_PELVIS_KEYS = ['ASIS', 'PSIS', 'L_ASIS', 'R_ASIS', 'L_PSIS', 'R_PSIS'] as const satisfies readonly PelvisPinKey[];

/** Remove ASIS/PSIS landmark coordinates from a partial custom blob (e.g. projection merge). */
export function omitPelvisLandmarksFromPartial(custom: Partial<CustomLandmarks> | undefined): Partial<CustomLandmarks> {
  if (!custom) return {};
  const out: Partial<CustomLandmarks> = { ...custom };
  for (const k of ALL_PELVIS_KEYS) {
    delete (out as Record<string, unknown>)[k];
  }
  return out;
}

type Point2D = { x: number; y: number };

/** Per-view pelvis pin keys (ASIS/PSIS) stored in `CapturedImage.custom`. */
export function pelvisPinKeysForView(view: CaptureSequence): PelvisPinKey[] {
  if (view === 'Anterior') return ['L_ASIS', 'R_ASIS'];
  if (view === 'Posterior') return ['L_PSIS', 'R_PSIS'];
  if (view === 'Left' || view === 'Right') return ['ASIS', 'PSIS'];
  return [];
}

/**
 * After pose re-inference, keep prior custom pins but replace **user-placed** pins with fresh
 * `inferPelvisPinsForView` output so "reset overlay" / reanalyze tracks new hip geometry.
 */
export function mergePelvisCustomAfterPoseReinfer(
  view: CaptureSequence,
  inferred: Partial<CustomLandmarks>,
  prevCustom: CustomLandmarks | undefined,
  prevMeta: PelvisPinsMeta | undefined
): CustomLandmarks {
  const next: CustomLandmarks = { ...inferred, ...(prevCustom || {}) };
  const keys = pelvisPinKeysForView(view);
  for (const k of keys) {
    const hadUser = prevMeta?.[k]?.source === 'user' && prevCustom?.[k];
    const inf = inferred[k];
    if (hadUser && inf) {
      (next as Record<string, unknown>)[k] = inf;
    }
  }
  return next;
}

/** Bump pelvis meta for pins that were refreshed from inference (still user-owned). */
export function pelvisMetaAfterRefreshedUserPins(
  view: CaptureSequence,
  inferred: Partial<CustomLandmarks>,
  prevCustom: CustomLandmarks | undefined,
  prevMeta: PelvisPinsMeta | undefined,
  baseMeta: PelvisPinsMeta
): PelvisPinsMeta {
  const keys = pelvisPinKeysForView(view);
  const now = new Date().toISOString();
  let out: PelvisPinsMeta = { ...baseMeta };
  for (const k of keys) {
    const hadUser = prevMeta?.[k]?.source === 'user' && prevCustom?.[k];
    if (hadUser && inferred[k]) {
      out = { ...out, [k]: { source: 'user', confidence: 1, updatedAt: now } };
    }
  }
  return out;
}

function clamp(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, n));
}

function isVisible(p: Landmark | undefined, minVis = 0.35): p is Landmark {
  if (!p) return false;
  const v = p.visibility ?? 1;
  return v >= minVis;
}

/**
 * Infer pelvis pins from pose landmarks.
 *
 * IMPORTANT:
 * - ASIS/PSIS are NOT directly detected by MediaPipe.
 * - This function provides a plausible starting guess so the UI can show pins immediately.
 * - Users should palpate and drag pins for accuracy.
 */
export function inferPelvisPinsForView(
  view: CaptureSequence,
  landmarks: Landmark[],
  opts?: { minVisibility?: number }
): Partial<CustomLandmarks> {
  const minVis = opts?.minVisibility ?? 0.35;

  // AP views: infer L/R ASIS/PSIS from hip landmarks by moving slightly medial and superior.
  if (view === 'Anterior' || view === 'Posterior') {
    const lHip = landmarks[IDX.L_HIP];
    const rHip = landmarks[IDX.R_HIP];
    if (!isVisible(lHip, minVis) || !isVisible(rHip, minVis)) return {};

    const hipWidthPx = Math.abs(rHip.x - lHip.x);
    if (!isFinite(hipWidthPx) || hipWidthPx < 10) return {};

    const inwardPx = hipWidthPx * 0.22; // move towards midline
    const upPx = hipWidthPx * 0.08; // move slightly superior
    const midX = (lHip.x + rHip.x) / 2;
    const towardMidX = (hipX: number): number => {
      const dir = Math.sign(midX - hipX);
      return dir === 0 ? hipX : hipX + dir * inwardPx;
    };

    const l: Point2D = { x: towardMidX(lHip.x), y: lHip.y - upPx };
    const r: Point2D = { x: towardMidX(rHip.x), y: rHip.y - upPx };

    if (view === 'Anterior') {
      return { L_ASIS: l, R_ASIS: r };
    }

    return { L_PSIS: l, R_PSIS: r };
  }

  // Lateral views: infer ASIS/PSIS from the visible hip with scaled anterior/posterior offsets.
  // Right view: anterior is +x. Left view: anterior is -x.
  if (view === 'Left' || view === 'Right') {
    const isLeft = view === 'Left';
    const hipIdx = isLeft ? IDX.L_HIP : IDX.R_HIP;
    const shoIdx = isLeft ? IDX.L_SHO : IDX.R_SHO;
    const kneeIdx = isLeft ? IDX.L_KNEE : IDX.R_KNEE;
    const hip = landmarks[hipIdx];
    if (!isVisible(hip, minVis)) return {};

    const shoulder = landmarks[shoIdx];
    const knee = landmarks[kneeIdx];

    // Use torso segment length as a scale reference.
    const torsoPx = isVisible(shoulder, minVis)
      ? Math.hypot(shoulder.x - hip.x, shoulder.y - hip.y)
      : isVisible(knee, minVis)
        ? Math.hypot(knee.x - hip.x, knee.y - hip.y)
        : 250; // fallback scale

    // Offsets tuned to look anatomically plausible across most adult bodies.
    const dx = clamp(torsoPx * 0.18, 26, 80); // anterior/posterior offset
    const dy = clamp(torsoPx * 0.06, 10, 36); // lift slightly superior vs hip joint center
    // Vertical separation between PSIS and ASIS. In most cases (neutral/apt),
    // PSIS sits slightly more superior than ASIS in a lateral photo.
    // We scale separation off dx so the implied tilt stays in a plausible range.
    const ySep = clamp(Math.abs(dx) * 0.14, 6, 18); // ~8° baseline (tan≈0.14)

    const anteriorDir = view === 'Right' ? 1 : -1;
    const yMid = hip.y - dy;

    return {
      // y increases downward on canvas. ASIS slightly lower => larger y.
      ASIS: { x: hip.x + anteriorDir * dx, y: yMid + ySep / 2 },
      PSIS: { x: hip.x - anteriorDir * dx, y: yMid - ySep / 2 },
    };
  }

  return {};
}


